import {Component, ElementRef, HostListener, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs/Subscription';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/timer';
import {EmployeeOnboardingService, EmployeeOnboardingSnapshot, OnboardingStandard, OnboardingTraining} from 'app/services/employee-onboarding.service';
import {prepareOnboardingStandard} from './onboarding-standard-navigation';
import {ONBOARDING_PANEL, OnboardingPanel, OnboardingStandardStep, OnboardingTrainingGroup,
  headTrainingVisible, onboardingFinished, onboardingVisible, probationVisible, startingSteps, trainingGroups} from './employee-onboarding-view';

const PANEL_TITLE = {onboarding: 'ONBOARDING', probation: 'PROBATION_TITLE', head: 'ONBOARDING_HEAD_TRAINING'};
const MANUAL_APPROVAL = 'MANUAL';
const COMPANY_MIDNIGHT = 'T00:00:00+03:00';
const MOBILE_WIDTH = 1024;
const MAX_TIMER_DELAY = 2147483647;
const NEXT_STEP = 1;
const NO_STANDARDS = 0;
const AFTER_RENDER = 0;
const TRIGGER_SELECTOR = '[data-onboarding-trigger]';
const ACCOUNT_SELECTOR = '#navbarDropdownMenuLink';

@Component({
  selector: 'app-employee-onboarding',
  templateUrl: './employee-onboarding.component.html',
  styleUrls: ['./employee-onboarding.component.css']
})
export class EmployeeOnboardingComponent implements OnInit, OnDestroy {
  readonly panels = ONBOARDING_PANEL;
  openedPanel: OnboardingPanel = null;
  private trigger: HTMLElement;
  private updates: Subscription;
  private navigation: Subscription;
  private expiryTimer: Subscription;
  private focusTimer: Subscription;
  private now = Date.now();
  private viewSnapshot: EmployeeOnboardingSnapshot;
  private standardStepItems: OnboardingStandardStep[] = [];
  private courseGroupItems: OnboardingTrainingGroup[] = [];

  constructor(public onboarding: EmployeeOnboardingService, private element: ElementRef, private router: Router) {}

  ngOnInit(): void {
    this.updates = this.onboarding.changes$.subscribe(() => this.refreshView());
    this.navigation = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) { this.close(); }
    });
    this.onboarding.start();
  }

  ngOnDestroy(): void {
    if (this.updates) { this.updates.unsubscribe(); }
    if (this.navigation) { this.navigation.unsubscribe(); }
    if (this.expiryTimer) { this.expiryTimer.unsubscribe(); }
    if (this.focusTimer) { this.focusTimer.unsubscribe(); }
    this.onboarding.clear();
  }

  get visible(): boolean { return this.showOnboarding || this.showProbation || this.showHeadTraining; }
  get opened(): boolean { return !!this.openedPanel; }
  get title(): string { return PANEL_TITLE[this.openedPanel]; }
  get showOnboarding(): boolean {
    return this.openedPanel === this.panels.ONBOARDING || this.onboarding.failed
      || onboardingVisible(this.onboarding.snapshot, this.now);
  }
  get showProbation(): boolean {
    return this.openedPanel === this.panels.PROBATION || this.onboarding.failed
      || probationVisible(this.onboarding.snapshot, this.now);
  }
  get onboardingDone(): boolean { return onboardingFinished(this.onboarding.snapshot); }
  get probationDone(): boolean { return !!this.onboarding.snapshot && this.onboarding.snapshot.approved; }
  get showHeadTraining(): boolean { return headTrainingVisible(this.onboarding.snapshot); }
  get headTrainings(): OnboardingTraining[] {
    return this.onboarding.snapshot && this.onboarding.snapshot.headTrainings || [];
  }
  get onboardingAttention(): boolean {
    const data = this.onboarding.snapshot;
    return !!data && !this.onboarding.failed && !this.onboardingDone
      && data.completedStandardsCount < data.assignedStandardsCount;
  }
  get probationAttention(): boolean {
    const data = this.onboarding.snapshot;
    return !!data && !this.onboarding.failed && this.onboardingDone && !data.approved
      && data.completedTrainingCount < data.requiredTrainingCount;
  }
  get standardSteps(): OnboardingStandardStep[] { this.refreshGroups(); return this.standardStepItems; }
  get courseGroups(): OnboardingTrainingGroup[] { this.refreshGroups(); return this.courseGroupItems; }
  get remainingStep(): number { return this.standardSteps.length + NEXT_STEP; }
  get allStandardsComplete(): boolean {
    const data = this.onboarding.snapshot;
    return !!data && data.assignedStandardsCount > NO_STANDARDS && data.completedStandardsCount >= data.assignedStandardsCount;
  }
  get nextStandardId(): string {
    if (this.onboardingDone) { return null; }
    const standards = this.standardSteps.reduce((all, step) => all.concat(step.standards), [] as OnboardingStandard[]);
    const next = standards.find(standard => !standard.completed);
    return next ? next.id : null;
  }
  get tenureComplete(): boolean {
    const date = this.onboarding.snapshot.eligibleAt;
    return !!date && Date.parse(date + COMPANY_MIDNIGHT) <= this.now;
  }
  get manualApproval(): boolean { return this.onboarding.snapshot.approvalSource === MANUAL_APPROVAL; }
  get companyCoursesInDepartment(): boolean {
    return this.onboarding.snapshot.trainings.some(course => course.companyRequired && this.departmentRequired(course));
  }

  toggle(panel: OnboardingPanel, trigger: HTMLElement): void {
    if (this.openedPanel === panel) { this.closeWithFocus(); return; }
    this.trigger = trigger;
    this.openedPanel = panel;
    if (!this.onboarding.loading) { this.onboarding.invalidate(); }
  }

  close(): void { this.openedPanel = null; }

  closeWithFocus(): void {
    this.close();
    this.deferFocus(() => this.focusTrigger());
  }

  openStandard(): void { prepareOnboardingStandard(); this.close(); }
  retry(event: MouseEvent): void {
    event.stopPropagation();
    this.focusTrigger();
    this.onboarding.invalidate();
  }
  retryCompletion(event: MouseEvent): void {
    event.stopPropagation();
    if (this.onboarding.loading || this.onboarding.completing) { return; }
    this.focusTrigger();
    this.onboarding.retryCompletion();
  }
  completedStep(step: OnboardingStandardStep): boolean {
    return !!step.standards.length && step.standards.every(standard => standard.completed);
  }
  courseComplete(course: OnboardingTraining): boolean { return course.currentLevel >= course.requiredLevel; }
  departmentRequired(course: OnboardingTraining): boolean { return !!course.departmentSources && !!course.departmentSources.length; }
  trackStandard(index: number, standard: OnboardingStandard): string { return standard.id; }
  trackTraining(index: number, training: OnboardingTraining): string { return training.specializationId; }
  trackStep(index: number, step: OnboardingStandardStep): number { return step.priority; }
  trackGroup(index: number, group: OnboardingTrainingGroup): string { return group.title; }
  trackSource(index: number, source: {id: string; name: string}): string { return source.id; }

  focusLeft(event: FocusEvent): void {
    if (!this.contains(event.relatedTarget)) { this.close(); }
  }

  @HostListener('document:click', ['$event'])
  outsideClick(event: MouseEvent): void {
    if (!this.contains(event.target)) { this.close(); }
  }

  @HostListener('keydown.escape', ['$event'])
  escape(event: KeyboardEvent): void {
    if (!this.opened) { return; }
    event.stopPropagation();
    this.closeWithFocus();
  }

  @HostListener('window:resize')
  resize(): void {
    if (this.element.nativeElement.ownerDocument.defaultView.innerWidth <= MOBILE_WIDTH) { this.close(); }
  }

  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  refreshView(): void {
    const focused = this.element.nativeElement.ownerDocument.activeElement as HTMLElement;
    const restoreFocus = !this.opened && this.contains(focused) && focused.matches(TRIGGER_SELECTOR);
    if (restoreFocus) { this.trigger = focused; }
    this.now = Date.now();
    this.closeCompletedHeadPanel();
    this.scheduleExpiry();
    if (restoreFocus) { this.deferFocus(() => this.focusTrigger()); }
  }

  private closeCompletedHeadPanel(): void {
    if (this.openedPanel === this.panels.HEAD && !this.onboarding.loading && !this.onboarding.failed
      && !this.showHeadTraining) {
      this.closeWithFocus();
    }
  }

  private scheduleExpiry(): void {
    if (this.expiryTimer) { this.expiryTimer.unsubscribe(); }
    const data = this.onboarding.snapshot;
    if (!data) { if (!this.onboarding.failed) { this.close(); } return; }
    const dates = [data.onboardingVisibleUntil, data.probationVisibleUntil, data.eligibleAt + COMPANY_MIDNIGHT]
      .map(date => Date.parse(date)).filter(date => date > this.now);
    if (!dates.length) { return; }
    const delay = Math.min(Math.min(...dates) - this.now, MAX_TIMER_DELAY);
    this.expiryTimer = Observable.timer(delay).subscribe(() => this.refreshView());
  }

  private refreshGroups(): void {
    const data = this.onboarding.snapshot;
    if (data === this.viewSnapshot) { return; }
    this.viewSnapshot = data;
    this.standardStepItems = data ? startingSteps(data) : [];
    this.courseGroupItems = data ? trainingGroups(data) : [];
  }

  private focusTrigger(): void {
    const doc = this.element.nativeElement.ownerDocument;
    if (this.trigger && doc.contains(this.trigger) && this.trigger.getClientRects().length) {
      this.trigger.focus();
      return;
    }
    const fallback = this.element.nativeElement.querySelector(TRIGGER_SELECTOR) || doc.querySelector(ACCOUNT_SELECTOR);
    if (fallback && fallback.getClientRects().length) { fallback.focus(); }
  }

  private deferFocus(action: () => void): void {
    if (this.focusTimer) { this.focusTimer.unsubscribe(); }
    this.focusTimer = Observable.timer(AFTER_RENDER).subscribe(action);
  }

  private contains(target: EventTarget): boolean {
    return !!target && this.element.nativeElement.contains(target);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/header/employee-onboarding/employee-onboarding.component.ts